import { Component } from '@theme/component';

class TraustonThreeTees extends Component {
  #controller;
  #products = new Map();
  #messages;
  #busy = false;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.#messages = JSON.parse(this.querySelector('[data-tee-messages]').textContent);
    this.addEventListener('change', this.#handleChange, { signal: this.#controller.signal });
    this.querySelector('[data-tee-form]').addEventListener('submit', this.#submit, { signal: this.#controller.signal });
    this.#updateTotal();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    super.disconnectedCallback();
  }

  #money(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: this.dataset.currency || 'USD' }).format(cents / 100);
  }

  #setOptions(select, values, placeholder, enabled = true) {
    const previous = select.value;
    select.replaceChildren(new Option(placeholder, ''));
    for (const value of values) select.add(new Option(value, value));
    select.disabled = !enabled;
    if (values.includes(previous)) select.value = previous;
  }

  #handleChange = async (event) => {
    if (!(event.target instanceof HTMLSelectElement)) return;
    const slot = event.target.closest('[data-tee-slot]');
    if (!slot || this.#busy) return;
    if (event.target.matches('[data-tee-style]')) {
      slot.productData = undefined;
      slot.variantData = undefined;
      const style = event.target;
      const selectedHandle = style.value;
      const color = slot.querySelector('[data-tee-color]');
      const size = slot.querySelector('[data-tee-size]');
      color.value = '';
      size.value = '';
      this.#setOptions(color, [], this.#messages.chooseColor, false);
      this.#setOptions(size, [], this.#messages.chooseSize, false);
      slot.querySelector('[data-tee-image]').hidden = true;
      slot.querySelector('[data-tee-details]').hidden = true;
      slot.querySelector('[data-tee-price]').textContent = selectedHandle ? this.#messages.loading : this.#messages.selectPrompt;
      this.#updateTotal();
      if (!selectedHandle) return;
      const productUrl = style.selectedOptions[0].dataset.url;
      try {
        if (!this.#products.has(productUrl)) {
          const response = await fetch(`${productUrl}.js`, { signal: this.#controller.signal });
          if (!response.ok) throw new Error('Product unavailable');
          this.#products.set(productUrl, await response.json());
        }
        if (!slot.isConnected || style.value !== selectedHandle) return;
        const product = this.#products.get(productUrl);
        slot.productData = product;
        slot.colorIndex = product.options.findIndex(option => (typeof option === 'string' ? option : option.name).toLowerCase() === 'color');
        slot.sizeIndex = product.options.findIndex(option => (typeof option === 'string' ? option : option.name).toLowerCase() === 'size');
        if (slot.colorIndex < 0 || slot.sizeIndex < 0) throw new Error('Options unavailable');
        const colors = [...new Set(product.variants.filter(variant => variant.available).map(variant => variant.options[slot.colorIndex]))];
        this.#setOptions(color, colors, this.#messages.chooseColor);
        const details = slot.querySelector('[data-tee-details]');
        details.href = productUrl;
        details.hidden = false;
        this.#updateImage(slot, product.featured_image, product.title);
        slot.querySelector('[data-tee-price]').textContent = this.#messages.selectPrompt;
      } catch (error) {
        if (error.name !== 'AbortError' && style.value === selectedHandle) slot.querySelector('[data-tee-price]').textContent = this.#messages.loadError;
      }
    }
    if (event.target.matches('[data-tee-color]')) {
      const color = event.target.value;
      const sizes = color ? [...new Set(slot.productData.variants.filter(variant => variant.available && variant.options[slot.colorIndex] === color).map(variant => variant.options[slot.sizeIndex]))] : [];
      this.#setOptions(slot.querySelector('[data-tee-size]'), sizes, this.#messages.chooseSize, Boolean(color));
    }
    this.#resolveVariant(slot);
    this.#updateTotal();
  };

  #updateImage(slot, source, title) {
    if (!source) return;
    const image = slot.querySelector('[data-tee-image]');
    const url = new URL(typeof source === 'string' ? source : source.src, document.baseURI);
    url.searchParams.set('width', '600');
    image.src = url.href;
    image.alt = title;
    image.hidden = false;
  }

  #resolveVariant(slot) {
    slot.variantData = undefined;
    if (!slot.productData) return;
    const color = slot.querySelector('[data-tee-color]').value;
    const size = slot.querySelector('[data-tee-size]').value;
    const variant = slot.productData.variants.find(candidate => candidate.options[slot.colorIndex] === color && candidate.options[slot.sizeIndex] === size && candidate.available);
    slot.variantData = variant;
    slot.querySelector('[data-tee-price]').textContent = variant ? this.#money(variant.price) : this.#messages.selectPrompt;
    if (variant?.featured_image) this.#updateImage(slot, variant.featured_image, `${slot.productData.title} — ${color}`);
  }

  #updateTotal() {
    const slots = [...this.querySelectorAll('[data-tee-slot]')];
    const chosen = slots.map(slot => slot.variantData).filter(Boolean);
    const total = chosen.reduce((sum, variant) => sum + variant.price, 0);
    this.querySelector('[data-tee-total]').textContent = chosen.length ? this.#money(total) : '—';
    this.querySelector('[data-tee-add]').disabled = this.#busy || chosen.length !== 3;
    if (this.#busy) return;
    this.querySelector('[data-tee-status]').textContent = chosen.length !== 3 ? this.#messages.selectPrompt : total >= 9900 && this.dataset.currency === 'USD' ? this.#messages.ready : this.#messages.below;
  }

  #submit = async (event) => {
    event.preventDefault();
    if (this.#busy) return;
    const chosen = [...this.querySelectorAll('[data-tee-slot]')].map(slot => slot.variantData);
    if (chosen.length !== 3 || chosen.some(variant => !variant?.available)) return;
    this.#busy = true;
    this.#updateTotal();
    this.querySelector('[data-tee-status]').textContent = this.#messages.adding;
    const quantities = new Map();
    for (const variant of chosen) quantities.set(variant.id, (quantities.get(variant.id) || 0) + 1);
    try {
      const response = await fetch(this.dataset.addUrl, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [...quantities].map(([id, quantity]) => ({ id, quantity })) }),
        signal: this.#controller.signal,
      });
      if (!response.ok) throw new Error('Cart update failed');
      window.location.assign(this.dataset.cartUrl);
    } catch (error) {
      if (error.name !== 'AbortError') this.querySelector('[data-tee-status]').textContent = this.#messages.error;
    }
  };
}

if (!customElements.get('trauston-three-tees')) customElements.define('trauston-three-tees', TraustonThreeTees);
